import React from 'react';
import { Calendar, Package, MapPin, DollarSign, FileText, CheckCircle, Users, Trash2, X } from 'lucide-react';
import { InternalNotesButton } from './InternalNotesButton';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  internalNotes?: string;
  isGen2Mode?: boolean;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
interface RentalsManagerProps {
  rentals: Rental[];
  filter: string;
  setFilter: (filter: string) => void;
  onRentalClick: (rental: Rental) => void;
  onCreateInvoice: (rental: Rental) => void;
  onAssignTechnician: (rental: Rental) => void;
  creatingInvoice: boolean;
  hasInvoice: (rentalId: string) => boolean;
  getInvoiceAmount: (rentalId: string) => number | null;
  getStatusColor: (status: string) => string;
  getDistributorColor: (distributor: string) => string;
  onNotesUpdated?: () => void;
}
export function RentalsManager({
  rentals,
  filter,
  setFilter,
  onRentalClick,
  onCreateInvoice,
  onAssignTechnician,
  creatingInvoice,
  hasInvoice,
  getInvoiceAmount,
  getStatusColor,
  getDistributorColor,
  onNotesUpdated
}: RentalsManagerProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const filteredRentals = rentals.filter(rental => {
    // Apply status filter
    if (filter !== 'all' && filter !== 'future' && filter !== 'past') {
      if (rental.status !== filter) return false;
    }
    // Apply time-based filter
    if (filter === 'future' || filter === 'past') {
      if (!rental.proposedEventDate) return filter === 'past'; // No date = past
      const eventDate = new Date(rental.proposedEventDate + 'T00:00:00');
      if (filter === 'future') {
        return eventDate >= today;
      } else {
        return eventDate < today;
      }
    }
    return true;
  });
  const groupedByMonthYear = filteredRentals.reduce((acc, rental) => {
    if (!rental.proposedEventDate) {
      if (!acc['No Date']) acc['No Date'] = [];
      acc['No Date'].push(rental);
      return acc;
    }
    const date = new Date(rental.proposedEventDate);
    const monthYear = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(rental);
    return acc;
  }, {} as Record<string, Rental[]>);
  const sortedMonthYears = Object.keys(groupedByMonthYear).sort((a, b) => {
    if (a === 'No Date') return 1;
    if (b === 'No Date') return -1;
    const dateA = new Date(groupedByMonthYear[a][0].proposedEventDate);
    const dateB = new Date(groupedByMonthYear[b][0].proposedEventDate);
    return dateA.getTime() - dateB.getTime();
  });
  return <>
      {/* Filters */}
      <div className="flex gap-2 p-6 border-b border-slate-200 overflow-x-auto" data-id="element-1630">
        <button onClick={() => setFilter('future')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'future' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1631">
          Future Events (
          {rentals.filter(r => {
          if (!r.proposedEventDate) return false;
          const eventDate = new Date(r.proposedEventDate + 'T00:00:00');
          return eventDate >= today;
        }).length}
          )
        </button>
        <button onClick={() => setFilter('past')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'past' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1632">
          Past Events (
          {rentals.filter(r => {
          if (!r.proposedEventDate) return true;
          const eventDate = new Date(r.proposedEventDate + 'T00:00:00');
          return eventDate < today;
        }).length}
          )
        </button>
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1633">
          All ({rentals.length})
        </button>
        <button onClick={() => setFilter('Pending')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Pending' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1634">
          Pending ({rentals.filter(r => r.status === 'Pending').length})
        </button>
        <button onClick={() => setFilter('Confirmed')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Confirmed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1635">
          Confirmed ({rentals.filter(r => r.status === 'Confirmed').length})
        </button>
        <button onClick={() => setFilter('Deposit')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Deposit' ? 'bg-yellow-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1636">
          Deposit ({rentals.filter(r => r.status === 'Deposit').length})
        </button>
        <button onClick={() => setFilter('Paid in Full')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Paid in Full' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1637">
          Paid in Full (
          {rentals.filter(r => r.status === 'Paid in Full').length})
        </button>
      </div>

      {/* Rentals Content */}
      <div className="p-6" data-id="element-1638">
        {filteredRentals.length === 0 ? <div className="flex items-center justify-center h-64" data-id="element-1639">
            <div className="text-center" data-id="element-1640">
              <Calendar size={48} className="text-slate-300 mx-auto mb-4" data-id="element-1641" />
              <p className="text-slate-600 text-lg font-medium" data-id="element-1642">
                No rentals found
              </p>
              <p className="text-slate-500 text-sm mt-1" data-id="element-1643">
                {filter !== 'all' ? 'Try changing the filter' : 'Rentals will appear here'}
              </p>
            </div>
          </div> : <div className="space-y-8" data-id="element-1644">
            {sortedMonthYears.map(monthYear => <div key={monthYear} data-id="element-1645">
                <div className="flex items-center gap-3 mb-4" data-id="element-1646">
                  <Calendar size={20} className="text-blue-600" data-id="element-1647" />
                  <h3 className="text-lg font-semibold text-slate-800" data-id="element-1648">
                    {monthYear}
                  </h3>
                  <span className="text-sm text-slate-500" data-id="element-1649">
                    ({groupedByMonthYear[monthYear].length} rental
                    {groupedByMonthYear[monthYear].length !== 1 ? 's' : ''})
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1650">
                  {groupedByMonthYear[monthYear].map(rental => <div key={rental.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow" data-id="element-1651">
                      <div className="flex items-start justify-between mb-3" data-id="element-1652">
                        <div className="flex-1 cursor-pointer" onClick={() => onRentalClick(rental)} data-id="element-1653">
                          <h4 className="font-semibold text-slate-800" data-id="element-1654">
                            {rental.name}
                          </h4>
                          <p className="text-sm text-slate-500" data-id="element-1655">
                            {rental.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1656">
                          <InternalNotesButton rentalId={rental.id} initialNotes={rental.internalNotes} onNotesSaved={onNotesUpdated} data-id="element-1657" />
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(rental.status)}`} data-id="element-1658">
                            {rental.status}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3 cursor-pointer" onClick={() => onRentalClick(rental)} data-id="element-1659">
                        <div className="flex items-center text-sm text-slate-600" data-id="element-1660">
                          <Package size={14} className="mr-2 text-slate-400" data-id="element-1661" />
                          <span className="font-medium" data-id="element-1662">
                            {rental.eventName}
                          </span>
                        </div>
                        {rental.proposedEventDate && <div className="flex items-center text-sm text-slate-600" data-id="element-1663">
                            <Calendar size={14} className="mr-2 text-slate-400" data-id="element-1664" />
                            <span className="font-medium text-blue-700" data-id="element-1665">
                              {(() => {
                      const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                      const startDateStr = startDate.toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                      });
                      if (rental.selections.numberOfDays > 1) {
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                        const endDateStr = endDate.toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric'
                        });
                        return `${startDateStr} - ${endDateStr}`;
                      }
                      return startDateStr;
                    })()}
                            </span>
                            <span className="ml-2 bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-0.5 rounded" data-id="element-1666">
                              {rental.selections.numberOfDays}{' '}
                              {rental.selections.numberOfDays === 1 ? 'Day' : 'Days'}
                            </span>
                          </div>}
                        <div className="flex items-center text-sm text-slate-600" data-id="element-1667">
                          <MapPin size={14} className="mr-2 text-slate-400" data-id="element-1668" />
                          <span className="truncate" data-id="element-1669">
                            {rental.customerInfo?.eventVenueCityState || [rental.customerInfo?.city, rental.customerInfo?.state].filter(Boolean).join(', ') || 'No location'}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-slate-600" data-id="element-1670">
                          <DollarSign size={14} className="mr-2 text-slate-400" data-id="element-1671" />
                          <span className="font-semibold" data-id="element-1672">
                            ${rental.total.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-slate-100 mb-3" data-id="element-1673">
                        <div className="flex flex-wrap gap-2 text-xs" data-id="element-1674">
                          {rental.selections.fullGen3Ring > 0 && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded" data-id="element-1675">
                              {rental.selections.fullGen3Ring}{' '}
                              {rental.isGen2Mode ? 'Gen 2' : 'Gen 3'} Ring
                              {rental.selections.fullGen3Ring !== 1 ? 's' : ''}
                            </span>}
                          {rental.selections.gen3Body > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded" data-id="element-1676">
                              {rental.selections.gen3Body}{' '}
                              {rental.isGen2Mode ? 'Gen 2' : 'Gen 3'} Body
                            </span>}
                          {rental.selections.gen3Head > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded" data-id="element-1677">
                              {rental.selections.gen3Head}{' '}
                              {rental.isGen2Mode ? 'Gen 2' : 'Gen 3'} Head
                            </span>}
                          {rental.selections.requireTech && <span className="bg-red-50 text-red-700 px-2 py-1 rounded" data-id="element-1678">
                              Tech
                            </span>}
                          {rental.selections.numberOfDays > 1 && <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded font-semibold" data-id="element-1679">
                              {rental.selections.numberOfDays} Days
                            </span>}
                        </div>
                        <div className="mt-2 text-xs font-semibold text-blue-700" data-id="element-1680">
                          Total Rings: {rental.selections.fullGen3Ring}
                        </div>
                        {rental.assignedTechnician && <div className="mt-2 flex items-center gap-2" data-id="element-1681">
                            <span className="text-xs text-slate-600" data-id="element-1682">
                              Tech:
                            </span>
                            <span className="text-xs font-medium text-slate-800" data-id="element-1683">
                              {rental.assignedTechnician}
                            </span>
                            {rental.assignedDistributor && <span className={`text-xs px-2 py-0.5 rounded border ${getDistributorColor(rental.assignedDistributor)}`} data-id="element-1684">
                                {rental.assignedDistributor}
                              </span>}
                          </div>}
                      </div>

                      <div className="space-y-2" data-id="element-1685">
                        {(rental.selections.requireTech || rental.selections.fullGen3Ring > 0 || rental.selections.gen3Body > 0 || rental.selections.gen3Head > 0 || rental.selections.gen3JudgesOnly > 0) && <button onClick={e => {
                  e.stopPropagation();
                  onAssignTechnician(rental);
                }} disabled={hasInvoice(rental.id)} className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${hasInvoice(rental.id) ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : rental.assignedTechnician ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-orange-600 text-white hover:bg-orange-700'}`} data-id="element-1686">
                            <Users size={16} data-id="element-1687" />
                            {rental.assignedTechnician ? 'Change Technician' : 'Assign Technician'}
                          </button>}

                        <button onClick={() => onCreateInvoice(rental)} disabled={hasInvoice(rental.id) || creatingInvoice || !rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me'} className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${hasInvoice(rental.id) || creatingInvoice || !rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me' ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-1688">
                          {creatingInvoice ? <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" data-id="element-1689"></div>
                              Creating...
                            </> : hasInvoice(rental.id) ? <>
                              <CheckCircle size={18} data-id="element-1690" />
                              <span className="text-xs" data-id="element-1691">
                                Invoice Created{' '}
                                {getInvoiceAmount(rental.id) !== null && `$${getInvoiceAmount(rental.id)!.toFixed(2)}`}
                              </span>
                            </> : <>
                              <FileText size={18} data-id="element-1692" />
                              Create Invoice
                            </>}
                        </button>

                        {(!rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me') && !hasInvoice(rental.id) && <p className="text-xs text-red-600 text-center" data-id="element-1693">
                              Assign a distributor to create invoice
                            </p>}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>}
      </div>
    </>;
}